import isEqual from "lodash/isEqual";

import api, { CacheTagType } from "@/store/api";
import { sortObservationsByDate } from "@/utils/vitals/weight.util";

interface GetWeightObservationsRequest {
  pageNumber: number;
  patientExternalId: string;
}

// Too small a page size will make excessive requests to the API
// Too large a page size will be overkill in presenting to the user
const DefaultPageSize = 60;

// Maximum number of pages to fetch to prevent memory issues
// This represents approximately 10 pages of data (600 items)
const MaxPageNumber = 10;

const weightTrackingApi = api.injectEndpoints({
  endpoints: (build) => ({
    getWeightObservations: build.query<
      PatientObservationsResponse,
      GetWeightObservationsRequest
    >({
      // forceRefetch() returns a boolean indicating whether to force an actual API call (ie: fetch)
      // rather than returning cached results directly. in this case, we force a refetch whenever
      // any of the arguments to the api call change (eg, pageNumber, patientExternalId, etc)
      // ie: ensuring the default RTK behavior remains, even though we're customizing merge
      forceRefetch: ({ currentArg, previousArg }) => {
        // === and !== check if the two object references point to the same address in memory
        // and NOT whether each of the properties for the object is equal
        // isEqual is used here to do a deep/nested property equality comparison
        return !isEqual(previousArg, currentArg);
      },
      // merge() specifies how to update cached data after every actual API request
      // (eg, useful when fetching new page numbers for the same cache when paging an API endpoint)
      // NOTE: the order of operations is queryFn() -> merge()
      // See: https://redux-toolkit.js.org/rtk-query/api/createApi#merge
      //
      // eslint-disable-next-line max-params
      merge: (currentCache, newItems) => {
        const currentIds = currentCache.data.map(
          (d) => d.observation.externalId,
        );
        // there seem to be various rare edge cases where a given RTK query will re-trigger
        // for the same argument, eg pageNumber:1, patientId: 123, and return the same results
        // as the last time this ran; in which case merging would result in duplicating these pieces of data
        newItems.data.forEach((d) => {
          const currentId = d.observation.externalId;
          const currentIndex = currentIds.indexOf(currentId);
          // if a result is already in the cache, update its state
          if (currentIds.includes(currentId)) {
            // eslint-disable-next-line no-param-reassign
            currentCache.data[currentIndex] = d;
            return;
          }
          // if a result has not been seen before, simply add it to the cache
          currentCache.data.push(d);
        });

        // NOTE: when using queryFn, transformations need to happen in queryFn itself
        // meaning any 'final merged cache transformations', like sorting the data, need to be repeated in merge()
        // eslint-disable-next-line no-param-reassign -- explicit mutation of the currentCache param is the goal
        currentCache.count = currentCache.data.length;
        // eslint-disable-next-line no-param-reassign -- explicit mutation of the currentCache param is the goal
        currentCache.data = sortObservationsByDate({
          data: currentCache.data,
          isDescending: true,
        });
      },
      // eslint-disable-next-line max-params
      providesTags: (_result, _error, arg) => [
        {
          id: arg.patientExternalId,
          type: CacheTagType.WeightObservations,
        },
      ],
      // queryFn is used instead of query to add custom logic before making the API call
      // Memory optimization: prevent API calls beyond MaxPageNumber to limit cache growth
      // eslint-disable-next-line max-params
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const { pageNumber, patientExternalId } = arg;

        // Prevent fetching beyond the maximum page limit
        if (pageNumber > MaxPageNumber) {
          return {
            data: {
              count: 0,
              data: [],
              pageNumber,
              pageSize: DefaultPageSize,
            },
          };
        }

        // Proceed with normal API call for pages within limit
        const result = await baseQuery({
          params: {
            "observation-type-name": "Weight",
            "page-number": pageNumber,
            "page-size": DefaultPageSize,
          },
          url: `/clinical-data/family/patients/${patientExternalId}/clinical-profile/observations`,
        });

        if (result.error) {
          return { error: result.error };
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- baseQuery returns unknown, we know it's PatientObservationsResponse
        const response = result.data as PatientObservationsResponse;

        // Transform response (sorting) - this replaces transformResponse when using queryFn
        const { count, data: unsortedData, pageSize } = response;
        return {
          data: {
            count,
            data: sortObservationsByDate({
              data: unsortedData,
              isDescending: true,
            }),
            pageNumber,
            pageSize,
          },
        };
      },
      // serializeQueryArgs is used to identify a unique cache, for some set of arguments / endpoint names
      // if QueryArgs are serialized, and no matching cache exists:
      //  - that means this query has never been invoked for these serialized QueryArgs
      //  - a new cache will be created for these serialized QueryArgs
      //  - an actual API call will be triggered, to initialize this new cache with actual data
      // if QueryArgs are serialized, and a matching cache does exist:
      //  - that means this query HAS been invoked for these serialized QueryArgs before
      //  - the existing cache for these serialized QueryArgs will be used
      //  - forceRefetch() will be called to determine whether or not to serve cached results, or trigger an actual API call
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // tell RTK to ignore pageNumber, pageSize args for the cache
        // so that all values of pageNumber/pageSize for the same patient
        // can share one big cache
        const { patientExternalId } = queryArgs;
        return { endpointName, patientExternalId };
      },
    }),
  }),
});

const { useGetWeightObservationsQuery } = weightTrackingApi;

export { useGetWeightObservationsQuery };
export default weightTrackingApi;
